/**
 * MerchantGroupCard — 一个商户账户组（同一 merchant_account_id + mode）的卡片（设计稿 PayerMax A1 / A4 / A5 / A6）。
 *
 * 组头：商户号（可复制）、mode、组内 App 数与「登记回调地址」；组级提示：未登记 / 全部停用 / 无可用验签材料时
 * 说明「回调全部被拒（401），退款和争议通知不会自动进入处理」；回调地址表：地址与 endpoint_id（可复制）、
 * 启停、验签材料份数与旧材料宽限、轮转 / 写入材料、停用 / 恢复、退役；其后是组内 App 配置行。
 * 回调按商户账户组配、凭据按 App 配：组内 App 共用回调地址与平台公钥，改一处影响整组。
 */

import type { PaymentProviderWebhookEndpointSummary } from '@novel-admin/shared';
import type { AppRow } from '~/data/queries';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { AppIcon } from '~/ui/AppIcon';
import { CopyableCode } from '~/ui/CopyableCode';
import { toast } from '~/ui/Toast';
import { ProviderAppConfigRow, type RowPendingAction } from './ProviderAppConfigRow';
import { ProviderNoticeBlock } from './ProviderNoticeBlock';
import { MaterialStats, ProviderResultCard } from './ProviderResultCard';
import {
  CONNECTION_STATUS_META,
  type MerchantGroup,
  chipClass,
  hasActiveVerifiedEndpoint,
} from './payment-provider-config-view';
import type { ProviderNotice } from './payment-provider-error-notice';

export type MerchantGroupResult =
  | { kind: 'registered'; endpointId: string; callbackUrl: string }
  | {
      kind: 'material';
      rotated: boolean;
      verifyingCount: number;
      retireAfter: string | null;
    };

export type EndpointPendingAction = 'activate' | 'deactivate' | 'retire' | null;

/** 复制到剪贴板：只在写入成功后提示「已复制」，失败如实提示，避免运营把旧剪贴板内容填进 PayerMax 后台。 */
async function copyText(value: string, label: string) {
  try {
    if (!navigator.clipboard) throw new Error('clipboard unavailable');
    await navigator.clipboard.writeText(value);
    toast(`已复制${label}`, { tone: 'pos', icon: 'IconCopy' });
  } catch {
    toast(`复制${label}失败：浏览器未授权剪贴板，请手动选中复制`, { tone: 'neg' });
  }
}

export function merchantGroupAnchorId(groupKey: string): string {
  return `merchant-group-${groupKey.replace(/[^A-Za-z0-9_-]/g, '-')}`;
}

export function MerchantGroupCard({
  group,
  appsById,
  writeBlockedReason,
  editBlockedReason,
  rowPending,
  rowNotices,
  endpointPending,
  registerPending,
  groupNotice,
  result,
  onRegister,
  onWriteMaterial,
  onEndpointState,
  onRetire,
  onDismissResult,
  onDismissGroupNotice,
  onTest,
  onActivate,
  onDeactivate,
  onEdit,
  onReplaceCredential,
  onDismissRowNotice,
}: {
  group: MerchantGroup;
  appsById: Map<string, AppRow>;
  writeBlockedReason: string | null;
  editBlockedReason: string | null;
  rowPending: Record<string, RowPendingAction>;
  rowNotices: Record<string, ProviderNotice>;
  endpointPending: Record<string, EndpointPendingAction>;
  registerPending: boolean;
  groupNotice: ProviderNotice | null;
  result: MerchantGroupResult | null;
  onRegister: () => void;
  onWriteMaterial: (endpoint: PaymentProviderWebhookEndpointSummary) => void;
  onEndpointState: (
    endpoint: PaymentProviderWebhookEndpointSummary,
    action: 'activate' | 'deactivate',
  ) => void;
  onRetire: (endpoint: PaymentProviderWebhookEndpointSummary) => void;
  onDismissResult: () => void;
  onDismissGroupNotice: () => void;
  onTest: (appId: string) => void;
  onActivate: (appId: string) => void;
  onDeactivate: (appId: string) => void;
  onEdit: (appId: string) => void;
  onReplaceCredential: (appId: string) => void;
  onDismissRowNotice: (appId: string) => void;
}) {
  const anchorId = merchantGroupAnchorId(group.key);
  const endpoints = group.endpoints;
  const activeEndpoints = endpoints.filter((e) => e.is_active);
  const groupLabel = `${group.merchantAccountId} · ${group.mode}`;
  const writeDisabled = writeBlockedReason !== null;
  const goToEndpoints = () =>
    document.getElementById(anchorId)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  const registeredEndpoint =
    result?.kind === 'registered'
      ? (endpoints.find((e) => e.endpoint_id === result.endpointId) ?? {
          endpoint_id: result.endpointId,
          callback_url: result.callbackUrl,
          is_active: true,
          verifying_material_count: 0,
          latest_retiring_retire_after: null,
        })
      : null;
  const retestConfigs =
    result?.kind === 'material'
      ? group.configs.filter((c) => c.connection_status !== 'connected')
      : [];

  const groupWarning =
    endpoints.length === 0
      ? {
          title: '未登记回调地址',
          body: '该组回调全部被拒（401），退款和争议通知不会自动进入处理。登记后把地址填入 PayerMax 后台，再写入平台公钥作为验签材料。',
        }
      : activeEndpoints.length === 0
        ? {
            title: '回调地址全部已停用',
            body: '该组回调全部被拒（401），退款和争议通知不会自动进入处理。恢复一个回调地址，或登记新的并写入验签材料。',
          }
        : !hasActiveVerifiedEndpoint(endpoints)
          ? {
              title: '启用中的回调地址还没有验签材料',
              body: '该组回调全部被拒（401），退款和争议通知不会自动进入处理。写入平台公钥作为验签材料后恢复。',
            }
          : null;

  return (
    <section
      id={anchorId}
      className="card"
      aria-label={`商户账户组 ${group.merchantAccountId}`}
      style={{ overflow: 'hidden' }}
    >
      <div
        className="hstack"
        style={{
          gap: 12,
          padding: '12px 16px',
          background: 'var(--bg-subtle)',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 7,
            background: 'var(--accent-soft)',
            color: 'var(--accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icons.IconBuilding size={15} />
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div className="label" style={{ fontSize: 10 }}>
            商户账户组
          </div>
          <div className="hstack" style={{ gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
            <span
              className="mono"
              style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--fg-strong)' }}
            >
              {group.merchantAccountId}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              aria-label="复制商户号"
              title="复制商户号"
              onClick={() => void copyText(group.merchantAccountId, '商户号')}
              style={{ width: 22, height: 22 }}
            >
              <Icons.IconCopy size={11} />
            </button>
            <span className="chip chip-info mono" style={{ fontSize: 10 }}>
              {group.mode}
            </span>
            <span className="chip" style={{ fontSize: 10 }}>
              {group.configs.length} 个 App
            </span>
            {group.configs.length > 1 && (
              <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                组内共用回调地址与平台公钥，改一个 App 的公钥会影响整组
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className={`btn btn-sm${endpoints.length === 0 && !writeDisabled ? ' btn-accent' : ''}`}
          disabled={writeDisabled || registerPending}
          title={writeBlockedReason ?? undefined}
          onClick={onRegister}
        >
          <Icons.IconPlus size={12} />
          {registerPending ? '登记中…' : '登记回调地址'}
        </button>
      </div>

      {(groupWarning || groupNotice || result) && (
        <div className="vstack" style={{ gap: 10, padding: '12px 16px 0' }}>
          {groupNotice && (
            <ProviderNoticeBlock notice={groupNotice} onDismiss={onDismissGroupNotice} />
          )}
          {result?.kind === 'registered' && registeredEndpoint && (
            <ProviderResultCard
              title="回调地址已登记"
              subtitle={
                <>
                  商户组 <span className="mono">{groupLabel}</span>
                </>
              }
              onDismiss={onDismissResult}
            >
              <div
                className="hstack"
                style={{
                  gap: 8,
                  marginTop: 12,
                  padding: '10px 12px',
                  background: 'var(--bg-inset)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                }}
              >
                <span
                  className="mono"
                  style={{
                    flex: 1,
                    fontSize: 12.5,
                    color: 'var(--fg-strong)',
                    wordBreak: 'break-all',
                  }}
                >
                  {result.callbackUrl}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-accent"
                  onClick={() => void copyText(result.callbackUrl, '回调地址')}
                >
                  <Icons.IconCopy size={12} />
                  复制地址
                </button>
              </div>
              <div
                style={{
                  marginTop: 10,
                  padding: '8px 10px',
                  background: 'var(--warn-soft)',
                  borderRadius: 'var(--radius)',
                  fontSize: 11.5,
                  lineHeight: 1.55,
                }}
              >
                把这个地址填入 PayerMax 后台的通知地址。<b>写入验签材料之前，回调一律被拒。</b>
              </div>
              <div className="hstack" style={{ gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-sm" onClick={onDismissResult}>
                  稍后
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-accent"
                  disabled={writeDisabled}
                  title={writeBlockedReason ?? undefined}
                  onClick={() => onWriteMaterial(registeredEndpoint)}
                >
                  下一步：写入验签材料
                </button>
              </div>
            </ProviderResultCard>
          )}
          {result?.kind === 'material' && (
            <ProviderResultCard
              title={result.rotated ? '验签材料已轮转' : '验签材料已写入'}
              subtitle={
                <>
                  商户组 <span className="mono">{groupLabel}</span>
                </>
              }
              onDismiss={onDismissResult}
            >
              <MaterialStats
                verifyingCount={result.verifyingCount}
                retireAfter={result.retireAfter}
              />
              {retestConfigs.length > 0 && (
                <div
                  style={{
                    marginTop: 14,
                    paddingTop: 10,
                    borderTop: '1px dashed var(--border)',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>
                    {result.rotated ? '逐个重新测试' : '逐个测试连接'}
                  </div>
                  <div className="vstack" style={{ gap: 6 }}>
                    {retestConfigs.map((c, index) => {
                      const app = appsById.get(c.app_id);
                      const meta = CONNECTION_STATUS_META[c.connection_status];
                      return (
                        <div key={c.app_id} className="hstack" style={{ gap: 8 }}>
                          <AppIcon app={app} label={app ? undefined : '?'} size="sm" />
                          <span style={{ fontSize: 12.5, flex: 1 }}>{app?.name ?? c.app_id}</span>
                          <span className={chipClass(meta.tone)} style={{ fontSize: 9.5 }}>
                            {meta.label}
                          </span>
                          <button
                            type="button"
                            className={`btn btn-sm${index === 0 ? ' btn-accent' : ''}`}
                            disabled={writeDisabled || (rowPending[c.app_id] ?? null) !== null}
                            onClick={() => onTest(c.app_id)}
                          >
                            {rowPending[c.app_id] === 'test' ? '测试中…' : '测试连接'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </ProviderResultCard>
          )}
          {groupWarning && (
            <div
              className="hstack"
              style={{
                gap: 10,
                alignItems: 'flex-start',
                padding: '10px 12px',
                background: 'var(--warn-soft)',
                borderRadius: 'var(--radius)',
              }}
            >
              <Icons.IconAlert size={14} style={{ color: 'var(--warn)', marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>{groupWarning.title}</div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: 'var(--fg-muted)',
                    lineHeight: 1.55,
                    marginTop: 2,
                  }}
                >
                  {groupWarning.body}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {endpoints.length > 0 && (
        <>
          <div style={{ padding: '10px 16px 4px' }} className="hstack">
            <span className="label" style={{ fontSize: 10.5 }}>
              回调地址
            </span>
            <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
              {endpoints.length}
            </span>
            <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
              · 未退役的 endpoint，按登记时间排序
            </span>
          </div>
          <div style={{ overflowX: 'auto', padding: '0 16px' }}>
            <table className="tbl" style={{ minWidth: 760 }}>
              <thead>
                <tr>
                  <th>回调地址</th>
                  <th>状态</th>
                  <th>验签材料</th>
                  <th style={{ textAlign: 'right' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {endpoints.map((endpoint) => {
                  const pending = endpointPending[endpoint.endpoint_id] ?? null;
                  const disabled = writeDisabled || pending !== null;
                  const hasMaterial = endpoint.verifying_material_count > 0;
                  return (
                    <tr key={endpoint.endpoint_id}>
                      <td style={{ height: 'auto', paddingTop: 8, paddingBottom: 8 }}>
                        <div className="hstack" style={{ gap: 6 }}>
                          <span
                            className="mono"
                            title={endpoint.callback_url}
                            style={{
                              fontSize: 11.5,
                              maxWidth: 330,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'inline-block',
                              color: endpoint.is_active ? undefined : 'var(--fg-muted)',
                            }}
                          >
                            {endpoint.callback_url}
                          </span>
                          {writeDisabled ? (
                            <button
                              type="button"
                              className="btn btn-sm"
                              aria-label="复制回调地址"
                              onClick={() => void copyText(endpoint.callback_url, '回调地址')}
                            >
                              <Icons.IconCopy size={11} />
                              复制
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-ghost btn-icon"
                              aria-label="复制回调地址"
                              title="复制回调地址"
                              onClick={() => void copyText(endpoint.callback_url, '回调地址')}
                              style={{ width: 22, height: 22 }}
                            >
                              <Icons.IconCopy size={11} />
                            </button>
                          )}
                        </div>
                        <CopyableCode
                          value={endpoint.endpoint_id}
                          label=" endpoint_id"
                          prefix="endpoint_id"
                          style={{ fontSize: 10, padding: 0 }}
                        />
                      </td>
                      <td>
                        <span
                          className={endpoint.is_active ? 'chip chip-pos' : 'chip'}
                          style={{ fontSize: 10 }}
                        >
                          {endpoint.is_active ? '● 启用中' : '已停用'}
                        </span>
                      </td>
                      <td style={{ height: 'auto', paddingTop: 8, paddingBottom: 8 }}>
                        {hasMaterial ? (
                          <>
                            <div style={{ fontSize: 12 }}>
                              <span className="mono">{endpoint.verifying_material_count}</span>{' '}
                              份参与验签
                            </div>
                            <div style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                              {endpoint.latest_retiring_retire_after ? (
                                <>
                                  旧材料宽限至{' '}
                                  <span className="mono">
                                    {fmtDateTime(endpoint.latest_retiring_retire_after)}
                                  </span>
                                </>
                              ) : (
                                '没有旧材料在宽限期'
                              )}
                            </div>
                          </>
                        ) : (
                          <span className="chip chip-warn" style={{ fontSize: 10 }}>
                            无验签材料 · 回调被拒
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="hstack" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            className={`btn btn-sm${!hasMaterial && !writeDisabled ? ' btn-accent' : ''}`}
                            disabled={disabled}
                            title={writeBlockedReason ?? undefined}
                            onClick={() => onWriteMaterial(endpoint)}
                          >
                            {hasMaterial ? '轮转验签材料' : '写入验签材料'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={disabled}
                            title={writeBlockedReason ?? undefined}
                            onClick={() =>
                              onEndpointState(
                                endpoint,
                                endpoint.is_active ? 'deactivate' : 'activate',
                              )
                            }
                          >
                            {pending === 'activate'
                              ? '恢复中…'
                              : pending === 'deactivate'
                                ? '停用中…'
                                : endpoint.is_active
                                  ? '停用'
                                  : '恢复'}
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm${writeDisabled ? '' : ' btn-danger'}`}
                            disabled={disabled}
                            title={writeBlockedReason ?? undefined}
                            onClick={() => onRetire(endpoint)}
                          >
                            {pending === 'retire' ? '退役中…' : '退役'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="card-flat" style={{ margin: '10px 16px 0', padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 10 }}>
              更换回调地址的顺序
            </div>
            <div className="vstack" style={{ gap: 10 }}>
              {[
                '登记新 endpoint',
                '给新 endpoint 写入验签材料',
                '在 PayerMax 后台改为新地址，并确认新地址已经收到回调',
                '再停用或退役旧 endpoint',
              ].map((text, index) => (
                <div key={text} className="hstack" style={{ gap: 10 }}>
                  <span
                    className="mono"
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 999,
                      background: 'var(--accent-soft)',
                      color: 'var(--accent-fg)',
                      fontSize: 10.5,
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </span>
                  <span style={{ fontSize: 12 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div style={{ padding: '12px 16px 4px' }} className="hstack">
        <span className="label" style={{ fontSize: 10.5 }}>
          App 配置
        </span>
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
          {group.configs.length}
        </span>
      </div>
      {group.configs.map((config) => (
        <ProviderAppConfigRow
          key={config.app_id}
          config={config}
          app={appsById.get(config.app_id)}
          endpoints={endpoints}
          writeBlockedReason={writeBlockedReason}
          editBlockedReason={editBlockedReason}
          pending={rowPending[config.app_id] ?? null}
          notice={rowNotices[config.app_id] ?? null}
          onTest={() => onTest(config.app_id)}
          onActivate={() => onActivate(config.app_id)}
          onDeactivate={() => onDeactivate(config.app_id)}
          onEdit={() => onEdit(config.app_id)}
          onReplaceCredential={() => onReplaceCredential(config.app_id)}
          onDismissNotice={() => onDismissRowNotice(config.app_id)}
          onGoToEndpoints={goToEndpoints}
        />
      ))}
    </section>
  );
}
